import { batch } from "solid-js";
import { ChannelUnread } from "../classes/ChannelUnread.js";
import { forgetChannelAck, loadChannelAcks, } from "../storage/ChannelAckStore.js";
import { ClassCollection } from "./Collection.js";
/**
 * Collection of Channel Unreads
 */
export class ChannelUnreadCollection extends ClassCollection {
    /**
     * Load unread information from server
     *
     * Merges locally remembered acks so a cold start does not revive unreads
     * when the server ack worker has not persisted yet.
     */
    async sync() {
        const unreads = await this.client.api.get("/sync/unreads");
        const userId = this.client.user.id;
        const cached = loadChannelAcks(userId);
        const replay = [];
        batch(() => {
            this.reset();
            for (const unread of unreads) {
                this.getOrCreate(unread._id.channel, unread);
            }
            for (const [channelId, messageId] of Object.entries(cached)) {
                const serverLast = this.get(channelId)?.lastMessageId ?? "0";
                if (serverLast.localeCompare(messageId) < 0) {
                    this.getOrCreate(channelId, {
                        _id: {
                            channel: channelId,
                            user: userId,
                        },
                        last_id: messageId,
                        mentions: [],
                    });
                    this.updateUnderlyingObject(channelId, "lastMessageId", messageId);
                    this.get(channelId)?.messageMentionIds.clear();
                    replay.push([channelId, messageId]);
                }
                else {
                    forgetChannelAck(userId, channelId);
                }
            }
        });
        await Promise.all(replay.map(([channelId, messageId]) => this.client.api
            .put(`/channels/${channelId}/ack/${messageId}`)
            .catch(() => undefined)));
    }
    /**
     * Flush debounced acks for every channel (call on pagehide)
     */
    flushPendingAcks() {
        for (const channel of this.client.channels.toList()) {
            channel.flushPendingAck();
        }
    }
    /**
     * Clear all unread data
     */
    reset() {
        this.updateUnderlyingObject({});
    }
    /**
     * Get or create
     * @param id Id
     * @param data Data
     */
    getOrCreate(id, data) {
        if (this.has(id)) {
            return this.get(id);
        }
        else {
            const instance = new ChannelUnread(this, id);
            this.create(id, "channelUnread", instance, this.client, data);
            return instance;
        }
    }
    /**
     * Get channel unread data for a specific Channel
     * @param channel Channel
     * @returns Unread
     */
    for(channel) {
        return this.getOrCreate(channel.id, {
            _id: {
                channel: channel.id,
                user: this.client.user.id,
            },
            last_id: null,
            mentions: [],
        });
    }
}
